/**
 * Colori e misure del design system (bozza approvata in docs/design-system/v0.1).
 * Unica fonte: il tema Chakra e tailwind.config.ts leggono da qui, così i due
 * sistemi di stile non possono divergere. Le regole d'uso stanno nel mockup.
 */

// Grigio freddo tinto verso il blu del brand: mai grigi neutri né nero puro
export const NEUTRAL = {
  0: '#FFFFFF',
  50: '#F1F3F6',
  100: '#E9EDF2',
  150: '#E2E7ED',
  200: '#D6DDE5',
  300: '#BCC5D0',
  400: '#8F9AA7',
  500: '#5E6A77',
  700: '#3A4654',
  800: '#2C3845',
  900: '#1C2733',
} as const;

export const BLUE = {
  50: '#EFF5FB',
  100: '#DEEAF6',
  200: '#BCD3EC',
  300: '#8DB3DC',
  400: '#5189C4',
  500: '#0056A6',
  600: '#004B90',
  700: '#003E76',
  800: '#03315C',
  900: '#0A2742',
} as const;

// Sagome della mappa: colori tenui ma distinguibili sulla base cartografica chiara.
export const FOOTPRINT_PALETTE = {
  palaces: BLUE[400],
  worship: '#9974AA',
  civic: '#BD904E',
  museums: '#63977C',
  hotels: '#BF796B',
  theaters: '#B76C92',
  landmarks: '#8A8064',
  infrastructure: '#4F949F',
  other: '#7E8993',
} as const;

// Il rosso è solo identità (header): mai per azioni o stati
export const BRAND_RED = '#E42017';
export const BRAND_RED_DARK = '#B81A12';
// Colori dei marchi Google e Facebook nei pulsanti di accesso social.
export const BRAND_GOOGLE = '#DB4437';
export const BRAND_FACEBOOK = '#1877F2';
export const FOOTER_BG = '#202A2E';

export const STATUS = {
  success: '#2E7D5B',
  successBg: '#E7F3EC',
  warning: '#8A5A00',
  warningBg: '#FBF2DF',
  danger: '#C0392F',
  dangerBg: '#FBEDEB',
  // Icona "in tendenza" nei chip delle card: unico accento caldo, solo sull'icona
  trend: '#D2691E',
} as const;

// 19 tipi di entità raggruppati in 6 famiglie: il colore distingue la famiglia, il nome del tipo resta scritto
export const FAMILY = {
  urbano: '#B5432C',
  architettura: '#6A4FA6',
  culto: '#9A3767',
  mare: '#16708B',
  cultura: '#8C5A12',
  persone: '#3A7255',
} as const;

// Periodi della mappa (colore delle foto): toni spenti come le famiglie, uno per periodo, stessa luminosità
export const PERIOD = {
  '1700-1859': '#8A7A66',
  '1860-1879': '#6B55A3',
  '1880-1899': '#A1497A',
  '1900-1914': '#B8452F',
  '1915-1918': '#BF6A2A',
  '1919-1924': '#A0812A',
  '1925-1938': '#4F8A5B',
  '1939-1945': '#2F6FA8',
  '1946-2025': '#5E6A77',
} as const;

/**
 * Scala `gray` di Chakra riempita coi neutri del sistema. I gradini 400 e 500
 * stanno tra quelli del sistema per non spostare di colpo il contrasto dei
 * ~1.000 usi diretti di gray.* nel codice; gray.600 è il testo secondario.
 */
export const CHAKRA_GRAY = {
  50: NEUTRAL[50],
  100: NEUTRAL[100],
  200: NEUTRAL[150],
  300: NEUTRAL[200],
  400: '#A3ADB9',
  500: '#7A8592',
  600: NEUTRAL[500],
  700: NEUTRAL[700],
  800: NEUTRAL[800],
  900: NEUTRAL[900],
} as const;

// Ruoli: i componenti nuovi usano solo questi nomi, mai i valori
export const ROLE = {
  pageBg: NEUTRAL[50],
  surface: NEUTRAL[0],
  surfaceSubtle: NEUTRAL[100],
  surfaceInverse: BLUE[900],
  border: NEUTRAL[150],
  borderStrong: NEUTRAL[200],
  borderHover: NEUTRAL[300],
  textPrimary: NEUTRAL[900],
  textBody: NEUTRAL[800],
  textSecondary: NEUTRAL[500],
  textTertiary: NEUTRAL[400],
  action: BLUE[500],
  actionHover: BLUE[700],
  actionSubtle: BLUE[50],
  actionSubtleBorder: BLUE[200],
  focus: BLUE[400],
} as const;

// Altezze dei controlli: nessun elemento interattivo ne usa altre
export const CONTROL_HEIGHT = { xs: '28px', sm: '36px', md: '44px', lg: '52px' } as const;

export const RADIUS = { sm: '6px', control: '8px', card: '12px' } as const;

// Nessuna ombra a riposo: solo al passaggio del mouse e per gli overlay
export const SHADOW = {
  hairline: '0 1px 1px rgba(28,39,51,.04)',
  raised: '0 1px 2px rgba(28,39,51,.04), 0 8px 20px -6px rgba(28,39,51,.12)',
  overlay: '0 16px 40px -8px rgba(28,39,51,.22)',
} as const;
